/**
 * AI Assistant Store
 *
 * Zustand store for managing AI assistant state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ChatMessage,
  ChatConversation,
  SuggestedAction,
  SmartInsight,
  ChatContext,
} from '../types/ai';

interface AIStore {
  // State
  isOpen: boolean;
  currentConversationId: string | null;
  conversations: ChatConversation[];
  isLoading: boolean;
  error: string | null;
  suggestedActions: SuggestedAction[];
  insights: SmartInsight[];
  context: ChatContext | null;

  // Actions - Drawer
  open: () => void;
  close: () => void;
  toggle: () => void;

  // Actions - Conversations
  createConversation: (title?: string) => string;
  setCurrentConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
  getCurrentConversation: () => ChatConversation | null;

  // Actions - Messages
  addMessage: (conversationId: string, message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  addUserMessage: (content: string) => void;
  addAssistantMessage: (content: string, suggestions?: SuggestedAction[], insights?: SmartInsight[]) => void;

  // Actions - Context
  setContext: (context: ChatContext | null) => void;
  updateContext: (partial: Partial<ChatContext>) => void;

  // Actions - Suggested Actions
  setSuggestedActions: (actions: SuggestedAction[]) => void;
  addSuggestedAction: (action: SuggestedAction) => void;
  removeSuggestedAction: (id: string) => void;
  clearSuggestedActions: () => void;

  // Actions - Insights
  setInsights: (insights: SmartInsight[]) => void;
  addInsight: (insight: SmartInsight) => void;
  dismissInsight: (id: string) => void;
  clearInsights: () => void;

  // Actions - Loading & Error
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Utility
  clear: () => void;
}

// Helper: Generate ID
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Helper: Generate conversation title from first message
const generateTitle = (firstMessage: string): string => {
  const maxLength = 50;
  if (firstMessage.length <= maxLength) return firstMessage;
  return firstMessage.substring(0, maxLength) + '...';
};

const useAIStore = create<AIStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isOpen: false,
      currentConversationId: null,
      conversations: [],
      isLoading: false,
      error: null,
      suggestedActions: [],
      insights: [],
      context: null,

      // Drawer actions
      open: () => {
        set({ isOpen: true, error: null });
        // Auto-create conversation if none exists
        const { conversations, currentConversationId } = get();
        if (conversations.length === 0 || !currentConversationId) {
          const id = get().createConversation();
          set({ currentConversationId: id });
        }
      },

      close: () => set({ isOpen: false }),

      toggle: () => {
        const { isOpen } = get();
        if (isOpen) {
          get().close();
        } else {
          get().open();
        }
      },

      // Conversation actions
      createConversation: (title?: string) => {
        const id = generateId();
        const now = new Date().toISOString();
        const conversation: ChatConversation = {
          id,
          title: title || 'New Conversation',
          messages: [],
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          conversations: [...state.conversations, conversation],
          currentConversationId: id,
        }));

        return id;
      },

      setCurrentConversation: (id: string) => {
        const { conversations } = get();
        const exists = conversations.find((c) => c.id === id);
        if (exists) {
          set({ currentConversationId: id, error: null });
        }
      },

      deleteConversation: (id: string) => {
        set((state) => {
          const filtered = state.conversations.filter((c) => c.id !== id);
          const newCurrentId =
            state.currentConversationId === id
              ? filtered.length > 0
                ? filtered[0].id
                : null
              : state.currentConversationId;

          return {
            conversations: filtered,
            currentConversationId: newCurrentId,
          };
        });
      },

      getCurrentConversation: () => {
        const { conversations, currentConversationId } = get();
        if (!currentConversationId) return null;
        return conversations.find((c) => c.id === currentConversationId) || null;
      },

      // Message actions
      addMessage: (conversationId, message) => {
        const id = generateId();
        const timestamp = new Date().toISOString();

        set((state) => {
          const conversations = state.conversations.map((conv) => {
            if (conv.id !== conversationId) return conv;

            const newMessage: ChatMessage = {
              ...message,
              id,
              timestamp,
            };

            const updatedMessages = [...conv.messages, newMessage];

            // Update conversation title from first user message
            let title = conv.title;
            if (message.role === 'user' && conv.messages.length === 0) {
              title = generateTitle(message.content);
            }

            return {
              ...conv,
              title,
              messages: updatedMessages,
              updatedAt: timestamp,
            };
          });

          return { conversations };
        });
      },

      addUserMessage: (content) => {
        const { currentConversationId, context } = get();
        if (!currentConversationId) return;

        get().addMessage(currentConversationId, {
          role: 'user',
          content,
          context: context || undefined,
        });
      },

      addAssistantMessage: (content, suggestions, insights) => {
        const { currentConversationId } = get();
        if (!currentConversationId) return;

        get().addMessage(currentConversationId, {
          role: 'assistant',
          content,
          suggestedActions: suggestions,
          insights,
        });

        // Update global suggestions and insights
        if (suggestions) {
          get().setSuggestedActions(suggestions);
        }
        if (insights) {
          get().setInsights(insights);
        }
      },

      // Context actions
      setContext: (context) => set({ context }),

      updateContext: (partial) => {
        set((state) => ({
          context: state.context ? { ...state.context, ...partial } : partial,
        }));
      },

      // Suggested actions
      setSuggestedActions: (actions) => set({ suggestedActions: actions }),

      addSuggestedAction: (action) => {
        set((state) => ({
          suggestedActions: [...state.suggestedActions, action],
        }));
      },

      removeSuggestedAction: (id) => {
        set((state) => ({
          suggestedActions: state.suggestedActions.filter((a) => a.id !== id),
        }));
      },

      clearSuggestedActions: () => set({ suggestedActions: [] }),

      // Insights actions
      setInsights: (insights) => set({ insights }),

      addInsight: (insight) => {
        set((state) => ({
          insights: [...state.insights, insight],
        }));
      },

      dismissInsight: (id) => {
        set((state) => ({
          insights: state.insights.map((i) =>
            i.id === id ? { ...i, dismissed: true } : i
          ),
        }));
      },

      clearInsights: () => set({ insights: [] }),

      // Loading & error
      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      // Utility
      clear: () => {
        set({
          currentConversationId: null,
          conversations: [],
          isLoading: false,
          error: null,
          suggestedActions: [],
          insights: [],
          context: null,
        });
      },
    }),
    {
      name: 'ai-assistant-storage',
      partialize: (state) => ({
        conversations: state.conversations,
        currentConversationId: state.currentConversationId,
      }),
    }
  )
);

export default useAIStore;
